import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ButtonIcon from '../../common/ButtonIcon/ButtonIcon';
import Button from '../../common/Buttons/Button';
import HomeworkDirection from './HomeworkDirection';
import './homework-card.scss';

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2.5" style={{ fill: 'none' }} />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" style={{ fill: 'none' }} />
  </svg>
);

const QuestionsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" style={{ fill: 'none' }} />
    <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8 1c0 1.7-2.4 2.1-2.4 3.6" style={{ fill: 'none' }} />
    <path d="M12 17.2h.01" style={{ fill: 'none' }} />
  </svg>
);

const AttemptsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 11a8 8 0 1 0-2.3 5.7" style={{ fill: 'none' }} />
    <path d="M20 4v7h-7" style={{ fill: 'none' }} />
  </svg>
);

const pluralUa = (count, one, few, many) => {
  const n = Math.abs(Number(count)) % 100;
  const n1 = n % 10;

  if (n > 10 && n < 20) return many;
  if (n1 === 1) return one;
  if (n1 >= 2 && n1 <= 4) return few;

  return many;
};

const HomeworkCard = ({ id, count, title, color, stage, variant, path, locked, lockedText, description, testsCount, attempts, isOpen, toggleAccordion, isContinue, load, homeworkElId, token, continueBtn, homeworkBlockId, direction, language }) => {
  const navigate = useNavigate();
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  const setAttemptsPage = (id) => {
    localStorage.setItem('homeworkId', JSON.stringify(id));
    navigate('/homework/reviews');
  };

  const setTestPage = (homeworkId, currentBLockId) => {
    localStorage.setItem('homeworkElId', JSON.stringify(homeworkId));
    localStorage.setItem('homeworkId', JSON.stringify(currentBLockId));
    localStorage.setItem('startTest', JSON.stringify(true));
    // Id старої спроби більше не актуальний: без цього F5 у момент старту
    // відкривав попередній блок замість нового.
    localStorage.removeItem('currentTestId');
    navigate('/homework/test');
  };

  const setNextTestPage = (homeworkBlockId) => {
    localStorage.setItem('currentTestId', JSON.stringify(homeworkBlockId));
    localStorage.setItem('homeworkId', JSON.stringify(id));
    // Прибираємо можливий "хвіст" перерваного старту, інакше "Продовжити"
    // стартував би нову спробу замість продовження поточної.
    localStorage.removeItem('startTest');
    navigate('/homework/test');
  };

  useEffect(() => {
    if (!path && !load && bodyRef.current) {
      setHeight(isOpen ? bodyRef.current.scrollHeight : 0);
    }
  }, [load, isOpen, path]);

  const questionsTotal = Number(testsCount) || 0;
  const attemptsTotal = Number(attempts) || 0;
  const attemptsLabel = `${attemptsTotal} ${pluralUa(attemptsTotal, 'спроба', 'спроби', 'спроб')}`;

  const openAttempts = (e) => {
    e.stopPropagation();
    setAttemptsPage(id);
  };

  const isEmptyReview = variant === 'review' && !locked && !continueBtn && questionsTotal === 0;

  const isActionsBelow = path && !locked && (stage === 'current' || Boolean(continueBtn));

  const isInlineDirection = Boolean(direction) && !path;

  const titleNode = (
    <div className={`homework-card__title ${isInlineDirection ? 'homework-card__title--dir' : ''}`.trim()}><span>{title}</span>{isInlineDirection && <HomeworkDirection direction={direction} language={language} />}</div>
  );

  return (
    <div className={`homework-card ${isOpen ? 'open' : ''} ${color} ${stage ? `homework-card--${stage}` : ''} ${variant ? `homework-card--${variant}` : ''} ${locked ? 'homework-card--locked' : ''} ${path ? 'homework-card--path' : ''} ${isActionsBelow ? 'homework-card--actions-below' : ''}`} data-homework-card={id}>
      <div className="homework-card__head" onClick={path ? undefined : () => toggleAccordion(id)}>
        <div className="homework-card__number">{count}</div>
        {path ? (<>
          <div className="homework-card__main">
            {titleNode}
            <div className="hw-meta">
              {questionsTotal > 0 && (
                <span className="hw-meta__item" title="Кількість питань">
                  <QuestionsIcon />
                  {questionsTotal} {pluralUa(questionsTotal, 'питання', 'питання', 'питань')}
                </span>
              )}
              {variant !== 'review' && (isContinue ? (
                <button type="button" className="hw-meta__item hw-meta__item--link" title="Переглянути спроби" onClick={openAttempts}>
                  <AttemptsIcon />
                  {attemptsLabel}
                </button>
              ) : (
                <span className="hw-meta__item" title="Кількість спроб">
                  <AttemptsIcon />
                  {attemptsLabel}
                </span>
              ))}
            </div>
          </div>
          {direction && <div className="homework-card__dir"><HomeworkDirection direction={direction} language={language} /></div>}
        </>) : titleNode}
        {!locked && continueBtn && <Button buttonBg='light' onClick={() => setNextTestPage(homeworkBlockId)}>Продовжити</Button>}
        {!locked && isEmptyReview && <span className="homework-card__empty">Помилок немає</span>}
        {locked ? (
          <span className="homework-card__lock" tabIndex={0} aria-label={lockedText} data-tip={lockedText} onClick={(e) => e.stopPropagation()}>
            <LockIcon />
          </span>
        ) : !isEmptyReview && (
          <Button buttonBg='green' btnSize='md' textSize='md' btnRadius='md' onClick={() => setTestPage(homeworkElId, id, token)}>{path && stage === 'done' ? 'Повторити' : 'Пройти'}</Button>
        )}
        {!path && <ButtonIcon icon='chevrone-down' iconColor='green' classes='homework-card__arrow' />}
      </div>
      {!path && <div
        className="homework-card__body"
        ref={bodyRef}
        style={{ maxHeight: `${height}px` }}
      >
        <div className="homework-card__content">
          <div className="homework-card-block">
            <div className="homework-card-block__title">Опис:</div>
            <div className="homework-card-block__desc">{description}</div>
          </div>
          <div className="homework-card-block">
            <div className="homework-card-block__title">Кількість тестів:</div>
            <div className="homework-card-block__desc">{testsCount}</div>
          </div>
          {variant !== 'review' && <div className="homework-card-block">
            <div className="homework-card-block__title">Кількість спроб:</div>
            <div className="homework-card-block__desc">
              <span className='homework-card-block__desc-num'>{attempts}</span>
              {isContinue && <Button buttonBg='light' onClick={() => setAttemptsPage(id)}>Переглянути</Button>}
            </div>
          </div>}
        </div>
      </div>}
    </div>
  );
};

HomeworkCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  color: PropTypes.string,
  stage: PropTypes.string,
  variant: PropTypes.string,
  path: PropTypes.bool,
  locked: PropTypes.bool,
  lockedText: PropTypes.string,
  description: PropTypes.string,
  testsCount: PropTypes.number,
  attempts: PropTypes.number,
  isOpen: PropTypes.bool,
  toggleAccordion: PropTypes.func,
  isContinue: PropTypes.bool,
  load: PropTypes.bool,
  homeworkElId: PropTypes.number,
  token: PropTypes.string,
  continueBtn: PropTypes.bool,
  homeworkBlockId: PropTypes.any,
  direction: PropTypes.string,
  language: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
  }),
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default HomeworkCard;